import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  Image,
  FileSpreadsheet,
  File,
  Trash2,
  Download,
  FolderOpen,
  Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const CATEGORIES = ["Contracts", "Inspections", "Disclosures", "Other"];

const CATEGORY_COLORS = {
  Contracts: { bg: "var(--t-primary)", text: "#fff" },
  Inspections: { bg: "#D97706", text: "#fff" },
  Disclosures: { bg: "#059669", text: "#fff" },
  Other: { bg: "var(--t-text-muted)", text: "#fff" },
};

const fileIcon = (mime) => {
  if (!mime) return File;
  if (mime.startsWith("image/")) return Image;
  if (mime.includes("pdf")) return FileText;
  if (mime.includes("spreadsheet") || mime.includes("excel") || mime.includes(".sheet")) return FileSpreadsheet;
  return File;
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const DocumentsPanel = ({ transactionId }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const fileRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("Other");
  const [activeTab, setActiveTab] = useState("All");

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/transactions/${transactionId}/documents`, { withCredentials: true });
      setDocuments(res.data);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [transactionId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25 MB limit");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", uploadCategory);

    try {
      await axios.post(`${API}/transactions/${transactionId}/documents`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded: ${file.name}`);
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/documents/${doc.document_id}/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
    try {
      await axios.delete(`${API}/documents/${doc.document_id}`, { withCredentials: true });
      toast.success("Document deleted");
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const canDelete = (doc) => isAdmin || doc.uploaded_by === user?.user_id;

  const tabs = ["All", ...CATEGORIES];
  const filtered = activeTab === "All" ? documents : documents.filter((d) => d.category === activeTab);
  const countByCategory = {};
  CATEGORIES.forEach((c) => { countByCategory[c] = documents.filter((d) => d.category === c).length; });

  return (
    <Card className="card-surface" data-testid="documents-card">
      <CardHeader className="border-b border-[var(--t-border)] py-3 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)] uppercase tracking-wider flex items-center gap-2">
            <FolderOpen className="w-4 h-4" />
            Documents
            {documents.length > 0 && (
              <span className="text-[10px] font-normal ml-1 px-1.5 py-0.5 rounded-full" style={{ background: "var(--t-border)", color: "var(--t-text-muted)" }}>
                {documents.length}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={uploadCategory} onValueChange={setUploadCategory}>
              <SelectTrigger className="h-7 text-xs w-[120px]" data-testid="upload-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              className="h-7 px-3 text-xs bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)] text-white"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="upload-document-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
              {uploading ? "Uploading..." : "Upload"}
            </Button>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              onChange={handleUpload}
              data-testid="upload-file-input"
            />
          </div>
        </div>
      </CardHeader>

      {/* Category Tabs */}
      <div className="flex gap-1 px-4 pt-3 pb-2 overflow-x-auto" style={{ borderBottom: "1px solid var(--t-border-light)" }}>
        {tabs.map((tab) => {
          const count = tab === "All" ? documents.length : countByCategory[tab] || 0;
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`doc-tab-${tab.toLowerCase()}`}
              className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap"
              style={{
                background: isActive ? "var(--t-primary)" : "transparent",
                color: isActive ? "#fff" : "var(--t-text-muted)",
              }}
            >
              {tab} {count > 0 && <span className="ml-1 opacity-70">({count})</span>}
            </button>
          );
        })}
      </div>

      <CardContent className="p-0">
        {loading ? (
          <div className="px-4 py-8 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: "var(--t-text-subtle)" }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <FolderOpen className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--t-border)" }} />
            <p className="text-sm" style={{ color: "var(--t-text-muted)" }}>
              {documents.length === 0 ? "No documents uploaded yet" : `No documents in "${activeTab}"`}
            </p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--t-border-light)" }}>
            {filtered.map((doc) => {
              const Icon = fileIcon(doc.mime_type);
              const catColor = CATEGORY_COLORS[doc.category] || CATEGORY_COLORS.Other;
              return (
                <div key={doc.document_id} className="px-4 py-3 flex items-center gap-3 group" data-testid={`doc-${doc.document_id}`}>
                  <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ background: "var(--t-bg)" }}>
                    <Icon className="w-4 h-4" style={{ color: "var(--t-text-muted)" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: "var(--t-text)" }}>
                      {doc.original_filename}
                    </p>
                    <p className="text-[11px] flex items-center gap-2 mt-0.5" style={{ color: "var(--t-text-subtle)" }}>
                      <span>{formatSize(doc.file_size)}</span>
                      <span>·</span>
                      <span>{doc.uploaded_by_name}</span>
                      <span>·</span>
                      <span>{formatDate(doc.uploaded_at)}</span>
                    </p>
                  </div>
                  <Badge
                    className="text-[10px] flex-shrink-0"
                    style={{ background: catColor.bg, color: catColor.text }}
                  >
                    {doc.category}
                  </Badge>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => handleDownload(doc)}
                      data-testid={`download-doc-${doc.document_id}`}
                    >
                      <Download className="w-3.5 h-3.5" style={{ color: "var(--t-text-muted)" }} />
                    </Button>
                    {canDelete(doc) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => handleDelete(doc)}
                        data-testid={`delete-doc-${doc.document_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
